import type {
  SessionRecapEvent,
  SessionRecapFailure,
  SessionRecapResponse,
  SessionRecapSource,
} from "../../../shared/session-recap";

// NO-591: `generated` is the durable Recap; `fixture` is the synthetic provider.
const SOURCES: ReadonlySet<string> = new Set<SessionRecapSource>([
  "fixture",
  "generated",
]);
const FAILURE_CODES: ReadonlySet<string> = new Set([
  "generation_failed",
  "retry_exhausted",
  "recap_unavailable",
]);
const PENDING_STATES: ReadonlySet<string> = new Set([
  "unavailable",
  "waiting_for_transcript",
  "generating",
]);

type CompletedRecap = Extract<SessionRecapResponse, { state: "completed" }>;
type PendingRecap = Extract<SessionRecapResponse, { state: "generating" }>;

/** Fixed message only: stream errors never carry event text. */
export class SessionRecapStreamError extends Error {
  constructor() {
    super("The recap stream could not be read.");
    this.name = "SessionRecapStreamError";
  }
}

function isObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function hasFailureCode(value: Record<string, unknown>): boolean {
  return typeof value.code === "string" && FAILURE_CODES.has(value.code);
}

function isFailure(value: unknown): value is SessionRecapFailure {
  if (!isObject(value) || !hasFailureCode(value)) return false;

  return typeof value.message === "string" && typeof value.retryAllowed === "boolean";
}

function hasMarkdown(value: unknown): value is { markdown: string } {
  return isObject(value) && typeof value.markdown === "string";
}

function isCompletedRecap(value: Record<string, unknown>): boolean {
  if (value.state !== "completed" || !hasMarkdown(value.recap)) return false;

  return (
    typeof value.transcriptVersion === "string" && typeof value.generatedAt === "string"
  );
}

function completedRecap(
  value: Record<string, unknown>,
  source: SessionRecapSource
): CompletedRecap | null {
  if (!isCompletedRecap(value)) return null;

  return {
    source,
    state: "completed",
    recap: { markdown: (value.recap as { markdown: string }).markdown },
    transcriptVersion: value.transcriptVersion as string,
    generatedAt: value.generatedAt as string,
  };
}

function failedRecap(
  value: Record<string, unknown>,
  source: SessionRecapSource
): SessionRecapResponse | null {
  if (value.state !== "failed" || !isFailure(value.failure)) return null;

  return { source, state: "failed", failure: copyFailure(value.failure) };
}

function copyFailure({
  code,
  message,
  retryAllowed,
}: SessionRecapFailure): SessionRecapFailure {
  return { code, message, retryAllowed };
}

function pendingRecap(
  value: Record<string, unknown>,
  source: SessionRecapSource
): SessionRecapResponse | null {
  if (typeof value.state !== "string" || !PENDING_STATES.has(value.state)) return null;

  return { source, state: value.state as PendingRecap["state"] };
}

type SourcedRecord = Record<string, unknown> & { source: SessionRecapSource };

function isSourcedRecord(value: unknown): value is SourcedRecord {
  return (
    isObject(value) && typeof value.source === "string" && SOURCES.has(value.source)
  );
}

/** Validates a durable Recap read; anything unexpected is null (fail closed). */
export function parseSessionRecapResponse(value: unknown): SessionRecapResponse | null {
  if (!isSourcedRecord(value)) return null;

  switch (value.state) {
    case "completed":
      return completedRecap(value, value.source);
    case "failed":
      return failedRecap(value, value.source);
    default:
      return pendingRecap(value, value.source);
  }
}

type EventParser = (value: SourcedRecord) => SessionRecapEvent | null;

const EVENT_PARSERS: ReadonlyMap<string, EventParser> = new Map<string, EventParser>([
  ["started", ({ source }) => ({ source, type: "started" })],
  ["heartbeat", ({ source }) => ({ source, type: "heartbeat" })],
  [
    "delta",
    (value) =>
      typeof value.text === "string"
        ? { source: value.source, type: "delta", text: value.text }
        : null,
  ],
  [
    "completed",
    (value) => {
      const recap = isObject(value.recap)
        ? completedRecap(value.recap, value.source)
        : null;

      return recap ? { source: value.source, type: "completed", recap } : null;
    },
  ],
  [
    "failed",
    (value) =>
      isFailure(value.failure)
        ? { source: value.source, type: "failed", failure: copyFailure(value.failure) }
        : null,
  ],
]);

function parseJson(line: string): unknown {
  try {
    return JSON.parse(line);
  } catch {
    throw new SessionRecapStreamError();
  }
}

function parseEvent(line: string): SessionRecapEvent {
  const value = parseJson(line);
  if (!isSourcedRecord(value) || typeof value.type !== "string")
    throw new SessionRecapStreamError();

  const event = EVENT_PARSERS.get(value.type)?.(value);
  if (!event) throw new SessionRecapStreamError();

  return event;
}

function isTerminal(event: SessionRecapEvent): boolean {
  return event.type === "completed" || event.type === "failed";
}

type StreamProgress = { terminal: boolean };

function* acceptLines(
  lines: string[],
  progress: StreamProgress
): Generator<SessionRecapEvent> {
  for (const line of lines) {
    if (!line.trim()) continue;

    if (progress.terminal) throw new SessionRecapStreamError();

    const event = parseEvent(line);
    progress.terminal = isTerminal(event);
    yield event;
  }
}

/**
 * NDJSON Recap events, validated one line at a time. Fails closed on a
 * missing body, malformed or unknown events, anything after the terminal
 * event, and EOF before one.
 */
export async function* readSessionRecapStream(
  body: ReadableStream<Uint8Array> | null
): AsyncGenerator<SessionRecapEvent> {
  if (!body) throw new SessionRecapStreamError();

  const reader = body.getReader();
  const decoder = new TextDecoder();
  const progress: StreamProgress = { terminal: false };
  let buffer = "";

  try {
    for (;;) {
      const { done, value } = await reader.read();
      buffer += decoder.decode(value, { stream: !done });
      const lines = buffer.split("\n");
      buffer = done ? "" : (lines.pop() ?? "");
      yield* acceptLines(lines, progress);
      if (done) break;
    }

    if (!progress.terminal) throw new SessionRecapStreamError();
  } finally {
    reader.releaseLock();
  }
}
